import _ from 'lodash';
import React from 'react';
import { authorize } from 'react-isomorphic-render';
import { Route, IndexRoute, Redirect } from 'react-router';
import BasicApp from './common/containers/basic-app';
import BasicClientAuthApp from './common/containers/basic-client-auth-app';
import Error from './components/error';
import { ACCOUNT_ACCESS } from 'app/app-constants';

/* Common Pages */
import PageNotFound from './components/page-not-found';
import Unauthorized from './components/unauthorized';

import CreateAccount from './containers/account/create';
import MarketingPage from './containers/marketing-page';
import NoGlowforge from './containers/no-glowforge';
import MarketingApp from './containers/root-apps/marketing-app';
import SettingsApp from './containers/root-apps/settings-app';

/** Account Settings Containers * */
import GeneralAccount from './containers/settings/general-account';
import Invite from './containers/settings/invite';
import Password from './containers/settings/password';
import Printers from './containers/settings/printers';
import AcceptInvite from './containers/settings/accept-invite';
import PurchaseHistory from './containers/settings/purchase-history';
import Subscription from './containers/settings/subscription';
import Users from './containers/settings/users';

/** Setup/Onboarding Pages * */
import SetupTermsAndConditions from './containers/setup/terms-and-conditions';
import Unboxing from './containers/setup/unboxing';
import AddGlowforgeContainer from './containers/setup/add-glowforge';
import SelectMachine from './containers/setup/select-machine';
import Instructions from './containers/setup/instructions';
import Exhaust from './containers/setup/exhaust';
import Finish from './containers/setup/finish';
import MachineName from './containers/setup/machine-name';

/** Landing Pages */
import LandingApp from './containers/landing/landing-app';
import GlowforgeLandingPageContainer from './containers/landing/glowforge-landing';

/** Support Containers * */
import ReferralPayment from './containers/support/referral-payment';

/** Glowforge Store Containers */
import Confirmation from './store/containers/glowforge/confirmation';

import * as constants from './route-constants';

const getUser = state => (_.isEmpty(state.common.user) ? undefined : state.common.user);
const getSubscription = state => (_.isEmpty(state.subscriptions) ? undefined : state.subscriptions.current);

const marketingIsVisible = (state) => {
  const visibility = _.get(state, 'marketing.marketingPage.visibility');
  const user = state.common.user;
  return !((visibility === 'logged-in-only' && _.isEmpty(user)) || (visibility === 'admin-only' && !_.get(user, 'is_admin')));
};

const allSettingsIsVisible = (state) => {
  const currentUser = getUser(state);
  // Not logged in, can't see page anyway
  if (!currentUser) return false;

  const currentSubscription = getSubscription(state);
  // Logged in but no subscription means definitely not a restricted user
  if (!currentSubscription && !currentSubscription.data) return true;

  // If logged in but has external values, they're the account owner
  // and not restricted.
  if (!_.isEmpty(currentSubscription, 'data.attributes.external')) return true;

  // Otherwise they're an added user and we need to check their account access
  if (currentSubscription.additional_users_access_status === ACCOUNT_ACCESS.RESTRICTED) return false;
}

// Restricts a `<Route/>` to logged-in users
const restricted = (route, authorization) => authorize(getUser, authorization, route);

const marketingVisibilityRestricted = (route, authorization) =>
  authorize(marketingIsVisible, authorization, route);

const addedUserVisibilityRestricted = (route, authorization) =>
  authorize(allSettingsIsVisible, authorization, route);

export default (
  <Route path=''>
    <Route path={constants.GLOWFORGE_LANDING_PAGE.BASE_URI} component={LandingApp}>
      <IndexRoute component={GlowforgeLandingPageContainer} />
    </Route>
    <Route path={constants.GLOWFORGE_LANDING_PAGE_HOME.BASE_URI} component={LandingApp}>
      <IndexRoute component={GlowforgeLandingPageContainer} />
    </Route>
    {/* Included to test CloudFront CDN compatibility */}
    <Route path={constants.GLOWFORGE_LANDING_PAGE_HOME_CDN_TEST.BASE_URI} component={LandingApp}>
      <IndexRoute component={GlowforgeLandingPageContainer} />
    </Route>
    <Route path={`${constants.GLOWFORGE_CONFIRMATION.BASE_URI}/:orderNumber`} component={BasicApp}>
      <IndexRoute component={Confirmation} />
    </Route>
    <Route path={'no_glowforge'} component={BasicApp}>
      <IndexRoute component={restricted(NoGlowforge)} />
    </Route>
    <Route path={constants.SETTINGS_ACCEPT_INVITE.BASE_URI} component={BasicApp}>
      <IndexRoute component={restricted(AcceptInvite)} />
    </Route>
    <Redirect from={constants.SETTINGS.BASE_URI} to='/settings/general' />
    <Route path={constants.SETUP.BASE_URI} component={BasicApp}>
      <Route path='terms-and-conditions' component={restricted(SetupTermsAndConditions)} />
      <Route path='machine/:serial/name' component={restricted(MachineName)} />
      <Route path='unboxing' component={restricted(Unboxing)} />
      <Route path='unboxing/:slug' component={restricted(Unboxing)} />
      <Route path='select' component={restricted(SelectMachine)} />
      <Route path='instructions' component={restricted(Instructions)} />
      <Route path='exhaust' component={restricted(Exhaust)} />
      <Route path='finished' component={restricted(Finish)} />
      <Route path='add_glowforge' component={restricted(AddGlowforgeContainer)} />
      <Redirect from={'first_glowforge'} to='terms-and-conditions?entry=first' />
    </Route>
    <Redirect from={constants.SETUP.BASE_URI} to={`${constants.SETUP.BASE_URI}/select`} />
    <Route path={constants.SETTINGS.BASE_URI} component={SettingsApp}>
      <Route path='general' component={restricted(GeneralAccount)} />
      <Route path='password' component={restricted(Password)} />
      <Route path='purchase-history' component={addedUserVisibilityRestricted(PurchaseHistory)} />
      <Route path='subscription' component={addedUserVisibilityRestricted(Subscription)} />
      <Route path='printers' component={addedUserVisibilityRestricted(Printers)} />
      <Route path='users' component={addedUserVisibilityRestricted(Users)} />
      <Route path='users/invite' component={addedUserVisibilityRestricted(Invite)} />
      <Route path='*' component={PageNotFound} status={404} />
    </Route>
    <Route path={constants.CREATE_ACCOUNT.BASE_URI} component={BasicApp}>
      <IndexRoute component={CreateAccount} />
    </Route>
    <Route path={constants.REFERRAL_PAYMENT.BASE_URI} component={BasicApp}>
      <IndexRoute component={restricted(ReferralPayment)} />
    </Route>
    <Route path={`${constants.MARKETING.BASE_URI}:slug`} component={MarketingApp}>
      <IndexRoute component={marketingVisibilityRestricted(MarketingPage)} />
    </Route>
    <Route path='error' component={BasicClientAuthApp} status={502}>
      <IndexRoute component={Error} status={502} />
    </Route>
    <Route path='unauthorized' component={BasicClientAuthApp} status={403}>
      <IndexRoute component={Unauthorized} status={403} />
    </Route>
    <Route path={constants.PAGE_NOT_FOUND.BASE_URI} component={BasicClientAuthApp} status={404}>
      <IndexRoute component={PageNotFound} status={404} />
    </Route>
    <Route path='*' component={BasicClientAuthApp} status={404}>
      <IndexRoute component={PageNotFound} status={404} />
    </Route>
  </Route>
);



// WEBPACK FOOTER //
// ./app/routes.jsx